/**
 * The shell's icon set: chunky, rounded 24×24 stroke icons drawn inline (no
 * icon font, no network). Decorative by default — the text next to an icon is
 * what screen readers hear.
 */
import type { ReactNode } from 'react';

export type IconName =
  | 'play'
  | 'trophy'
  | 'settings'
  | 'user'
  | 'back'
  | 'close'
  | 'check'
  | 'star'
  | 'home'
  | 'refresh'
  | 'sound'
  | 'mute'
  | 'globe'
  | 'pencil'
  | 'hand'
  | 'eye'
  | 'lock'
  | 'bag'
  | 'watch'
  | 'sparkle'
  | 'chevron'
  | 'info'
  | 'school'
  | 'pin'
  | 'trash'
  | 'sticker'
  | 'shuffle'
  | 'wifi-off'
  | 'plus';

const PATHS: Record<IconName, ReactNode> = {
  play: <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5Z" fill="currentColor" />,
  trophy: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H4.5a2.5 2.5 0 0 0 2.6 3.9M17 6h2.5a2.5 2.5 0 0 1-2.6 3.9M12 14v3.5M8 20.5h8M9.5 17.5h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M4.2 7.5l2.1 1.2M17.7 15.3l2.1 1.2M4.2 16.5l2.1-1.2M17.7 8.7l2.1-1.2" />
      <circle cx="12" cy="12" r="7" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="4" />
      <path d="M4.5 20.5c1.2-4 4.1-6 7.5-6s6.3 2 7.5 6" />
    </>
  ),
  back: <path d="M14.5 5.5 8 12l6.5 6.5M8.5 12H20" />,
  close: <path d="m6.5 6.5 11 11M17.5 6.5l-11 11" />,
  check: <path d="m4.5 12.5 4.5 4.5 10.5-10.5" />,
  star: (
    <path
      d="m12 3.2 2.6 5.5 6 .8-4.4 4.1 1.1 6-5.3-2.9-5.3 2.9 1.1-6-4.4-4.1 6-.8L12 3.2Z"
      fill="currentColor"
      strokeLinejoin="round"
    />
  ),
  home: (
    <>
      <path d="M3.5 11 12 4l8.5 7" />
      <path d="M6 9.5v10h4.5v-5h3v5H18v-10" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.2-5.3" />
      <path d="M19.5 4.5v4h-4" />
    </>
  ),
  sound: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4v-5Z" fill="currentColor" />
      <path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.5a7.8 7.8 0 0 1 0 11" />
    </>
  ),
  mute: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4v-5Z" fill="currentColor" />
      <path d="m15.5 9.5 5 5M20.5 9.5l-5 5" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.5 2.4 3.6 5.2 3.6 8.5s-1.1 6.1-3.6 8.5c-2.5-2.4-3.6-5.2-3.6-8.5S9.5 5.9 12 3.5Z" />
    </>
  ),
  pencil: (
    <>
      <path d="m15.5 5 3.5 3.5L9 18.5l-4.5 1 1-4.5L15.5 5Z" />
      <path d="m13.5 7 3.5 3.5" />
    </>
  ),
  hand: (
    <path d="M8.5 12.5V6a1.5 1.5 0 0 1 3 0v5.5M11.5 11V4.5a1.5 1.5 0 0 1 3 0V11M14.5 11V6a1.5 1.5 0 0 1 3 0v7c0 4-2.5 7-6 7-2.6 0-4-1.3-5.4-3.4L3.8 14a1.5 1.5 0 0 1 2.4-1.8l2.3 2.3" />
  ),
  eye: (
    <>
      <path d="M2.8 12S6 5.5 12 5.5 21.2 12 21.2 12 18 18.5 12 18.5 2.8 12 2.8 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2.5" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
  bag: (
    <>
      <path d="M5 8h14l-1.2 12H6.2L5 8Z" />
      <path d="M9 10V7a3 3 0 0 1 6 0v3" />
    </>
  ),
  watch: (
    <>
      <rect x="6.5" y="6" width="11" height="12" rx="3.5" />
      <path d="M9 6l.8-3h4.4l.8 3M9 18l.8 3h4.4l.8-3M17.5 10.5h1.5" />
    </>
  ),
  sparkle: (
    <path
      d="M12 3.5c.7 4.3 2.2 5.8 6.5 6.5-4.3.7-5.8 2.2-6.5 6.5-.7-4.3-2.2-5.8-6.5-6.5 4.3-.7 5.8-2.2 6.5-6.5ZM18.5 15.5c.3 1.8.9 2.4 2.7 2.7-1.8.3-2.4.9-2.7 2.7-.3-1.8-.9-2.4-2.7-2.7 1.8-.3 2.4-.9 2.7-2.7Z"
      fill="currentColor"
    />
  ),
  chevron: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5M12 7.6v.1" />
    </>
  ),
  school: (
    <>
      <path d="M2.5 9.5 12 5l9.5 4.5L12 14 2.5 9.5Z" />
      <path d="M6.5 11.5v4.5c1.5 1.6 3.4 2.4 5.5 2.4s4-.8 5.5-2.4v-4.5M21.5 9.5v5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11Z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  trash: (
    <>
      <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l1 13h9l1-13" />
      <path d="M10 11v5.5M14 11v5.5" />
    </>
  ),
  sticker: (
    <>
      <path d="M20.5 12.5A8.5 8.5 0 1 1 11.5 3.5c0 5 4 9 9 9Z" />
      <path d="M11.5 3.5c0 5 4 9 9 9-4.5 0-9 .5-9-9Z" />
    </>
  ),
  shuffle: (
    <>
      <path d="M3.5 7.5h3.2c4.6 0 5.9 9 10.6 9h3.2M3.5 16.5h3.2c1.6 0 2.8-1.1 3.8-2.6M13.5 10c1-1.5 2.2-2.5 3.8-2.5h3.2" />
      <path d="m18 5 2.5 2.5L18 10M18 14l2.5 2.5L18 19" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M3 8.5a13 13 0 0 1 4.3-2.6M10.5 5.2A13 13 0 0 1 21 8.5M6 12a8.5 8.5 0 0 1 3.1-2M14.5 10.2a8.5 8.5 0 0 1 3.5 1.8M9 15.5a4.3 4.3 0 0 1 6 0" />
      <circle cx="12" cy="19" r="1" fill="currentColor" />
      <path d="m3.5 3.5 17 17" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
};

export interface IconProps {
  name: IconName;
  /** CSS size (default 1.25em, so icons scale with the text around them). */
  size?: string;
  className?: string;
  /** Give the icon a name only when it stands alone without visible text. */
  label?: string;
}

export function Icon({ name, size = '1.25em', className, label }: IconProps) {
  return (
    <svg
      className={className ? `ui-icon ${className}` : 'ui-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
